import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "./ui/dialog";
import { APP_FONT_STACK } from "../utils/common";

/**
 * ConfirmDialog — the shadcn-Dialog replacement for the legacy ConfirmBox.
 *
 * It keeps ConfirmBox's prop API so migrating a call site is (almost) a pure
 * rename: `<ConfirmBox ... />` → `<ConfirmDialog ... />`. On top of the old
 * props it adds an optional `title` (bold heading) and, for free from the
 * Dialog primitive, a focus trap, Escape / backdrop close, scroll-lock, the
 * blurred backdrop, and a × close button.
 *
 * Copy in = copy out:
 *   text                → the message (becomes the heading when no `title`)
 *   title               → optional bold heading; then `text` becomes the body
 *   extraText           → the small orange pill above the heading (error codes)
 *   buttonText/Handler  → the primary (orange) button; defaults to closing
 *   extraButton*        → a secondary (gray) button
 *   thirdButton* + …Condition → a conditional secondary (gray) button
 *   showButton={false}  → blocking mode: no footer, no ×, can't be dismissed
 *                         (the parent closes it by flipping `open`)
 *   dismissible={false} → keep the footer/button, but block ×, Escape and
 *                         outside-click so the ONLY way to close is a button
 *   open / setOpen      → controlled visibility
 *   selectedLang        → per-language font, same as everywhere else
 *
 * Example:
 *   <ConfirmDialog
 *     open={openError}
 *     setOpen={setOpenError}
 *     text={errorMsg}
 *     buttonText={t("Close")}
 *   />
 */

const PRIMARY_BTN =
  "w-full rounded-lg bg-orange-150 px-5 py-2.5 text-base font-semibold text-white hover:bg-orange-150/90 sm:w-auto";
const SECONDARY_BTN =
  "w-full rounded-lg bg-gray-100 px-5 py-2.5 text-base font-semibold text-black-150 hover:bg-gray-200 sm:w-auto";

export default function ConfirmDialog({
  open,
  setOpen,
  title,
  text,
  extraText,
  buttonText,
  buttonHandler,
  extraButtonText,
  extraButtonHandler,
  thirdButtonText,
  thirdButtonHandler,
  thirdButtonCondition = false,
  showButton = true,
  dismissible = true,
  selectedLang,
}) {
  const fontFamily = APP_FONT_STACK;

  // Two independent controls:
  //  • showButton={false} — ConfirmBox's blocking mode (e.g. network-unstable):
  //    no footer at all.
  //  • dismissible={false} — keep the footer/button, but block the ×, Escape and
  //    outside-click so the ONLY way to close is a button handler.
  const canDismiss = showButton && dismissible;
  const close = () => setOpen && setOpen(false);
  const onPrimary = buttonHandler || close;

  // Radix requires a DialogTitle. With no explicit `title`, the message itself
  // becomes the heading (and there's no separate body).
  const heading = title || text;
  const body = title ? text : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && canDismiss) close();
      }}
    >
      <DialogContent
        style={{ fontFamily }}
        showClose={canDismiss}
        {...(!canDismiss && {
          onInteractOutside: (e) => e.preventDefault(),
          onEscapeKeyDown: (e) => e.preventDefault(),
        })}
      >
        <DialogHeader>
          {extraText && (
            <span className="mb-2 w-max rounded bg-[#FFF2ED] px-3 py-1 text-sm font-semibold text-orange-150">
              {extraText}
            </span>
          )}
          <DialogTitle>{heading}</DialogTitle>
        </DialogHeader>

        {body && (
          <DialogBody>
            <p className="mt-3 text-base leading-relaxed text-gray-150">{body}</p>
          </DialogBody>
        )}

        {showButton && (
          <DialogFooter className="mt-6">
            {extraButtonText && extraButtonHandler && (
              <button
                type="button"
                onClick={extraButtonHandler}
                className={SECONDARY_BTN}
              >
                {extraButtonText}
              </button>
            )}
            {thirdButtonCondition && thirdButtonText && (
              <button
                type="button"
                onClick={thirdButtonHandler}
                className={SECONDARY_BTN}
              >
                {thirdButtonText}
              </button>
            )}
            {buttonText && (
              <button type="button" onClick={onPrimary} className={PRIMARY_BTN}>
                {buttonText}
              </button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
